import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getBrand,
  resolveBrandId,
  resolveBrandIdFromHost,
  type BrandDefinition,
  type BrandId,
} from '@/shared/config/brand';
import { syncFavicon } from '@/shared/lib/favicon';
import { useTheme } from '@/shared/lib/theme';

interface PartnerHint {
  partner?: boolean;
  partnerName?: string | null;
}

interface BrandContextValue {
  brand: BrandDefinition;
  brandId: BrandId;
  /** Marca derivada só do Host (login / telas sem loja). */
  hostBrandId: BrandId;
  /** Sobrescreve com partner do passbook (null limpa). */
  setPartnerHint: (hint: PartnerHint | null) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

function currentHostname(): string {
  if (typeof window === 'undefined') return '';

  if (import.meta.env.DEV) {
    const previewBrand = new URLSearchParams(window.location.search).get('brand');
    if (previewBrand === 'webautomacao' || previewBrand === 'goomer') {
      window.sessionStorage.setItem('fixe-preview-brand', previewBrand);
    }

    const activePreviewBrand =
      previewBrand ?? window.sessionStorage.getItem('fixe-preview-brand');
    if (activePreviewBrand === 'webautomacao') return 'fidelidade.pdvlegal.com.br';
    if (activePreviewBrand === 'goomer') return 'loyalty.goomer.app';
  }

  return window.location.hostname;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const { resolvedTheme } = useTheme();
  const [hostname] = useState(currentHostname);
  const [partnerHint, setPartnerHintState] = useState<PartnerHint | null>(null);

  const hostBrandId = useMemo(() => resolveBrandIdFromHost(hostname), [hostname]);

  const brandId = useMemo(
    () =>
      resolveBrandId({
        hostname,
        partner: partnerHint?.partner,
        partnerName: partnerHint?.partnerName,
      }),
    [hostname, partnerHint],
  );

  const brand = useMemo(() => getBrand(brandId), [brandId]);

  const setPartnerHint = useCallback((hint: PartnerHint | null) => {
    setPartnerHintState(hint);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.brand = brand.id;
    root.style.setProperty('--brand-primary', brand.theme.primary);
    root.style.setProperty('--brand-primary-foreground', brand.theme.primaryForeground);
    root.style.setProperty('--brand-hue', brand.theme.hue);
    document.title = brand.documentTitle;
    const metaAuthor = document.querySelector('meta[name="author"]');
    if (metaAuthor) metaAuthor.setAttribute('content', brand.metaAuthor);
    syncFavicon(resolvedTheme, brand.assets);
  }, [brand, resolvedTheme]);

  const value = useMemo<BrandContextValue>(
    () => ({
      brand,
      brandId,
      hostBrandId,
      setPartnerHint,
    }),
    [brand, brandId, hostBrandId, setPartnerHint],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand deve ser usado dentro de BrandProvider');
  return ctx;
}

/**
 * Aplica hint de partner do passbook enquanto a loja estiver montada.
 * Fora de /loja/:id, limpa para voltar ao Host.
 * Quem chama deve omitir o hint para Admin em inspeção (mantém marca do Host).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSyncBrandFromPassbook(hint: PartnerHint | null) {
  const { setPartnerHint } = useBrand();
  const partner = hint?.partner;
  const partnerName = hint?.partnerName ?? null;
  const active = hint != null;

  useEffect(() => {
    if (!active) {
      setPartnerHint(null);
      return () => setPartnerHint(null);
    }
    setPartnerHint({ partner, partnerName });
    return () => setPartnerHint(null);
  }, [active, partner, partnerName, setPartnerHint]);
}
